import { Box, IconButton, InputBase, Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { ATCOPaletteColor } from '../theme/ATCOPaletteColor';
import useResponsiveHelper from '../components/common/hooks/useResponsiveHelper';

interface IProps {
    doSearch?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLInputElement>) => void;
    searchTerm?: () => void;
    setSearchValue: (value: string) => void;
    searchValue?: string;
    placeholder?: string;
    fullWidth?: boolean;
}

const useStyles = makeStyles((theme) => ({
    searchBar: (fullWidth) => ({
        display: 'flex',
        height: 55,
        width: fullWidth ? '100%' : '916px',
        [theme.breakpoints.down('tablet')]: {
            width: fullWidth ? '100%' : '382px',
        },
        [theme.breakpoints.only('tablet')]: {
            width: fullWidth ? '100%' : '704px',
        },
    }),
    searchText: (fullWidth) => ({
        color: theme.palette.primary.main,
        width: fullWidth ? '100%' : '820px',
        [theme.breakpoints.down('tablet')]: {
            width: fullWidth ? '100%' : '286px',
        },
        [theme.breakpoints.only('tablet')]: {
            width: fullWidth ? '100%' : '608px',
        },
    }),
    externalLink: {
        '&:hover': { color: ATCOPaletteColor.white },
        color: ATCOPaletteColor.primary1Tint1,
        marginLeft: '10px',
    },
    searchIcon: {
        width: '24px',
        height: '24px',
        color: ATCOPaletteColor.shadeBlack,
    },
    textLink: {
        color: ATCOPaletteColor.white,
        textDecoration: 'none',
        '&:hover': {
            color: ATCOPaletteColor.primary2Tint4,
        },
    },
    atcoLogo: {
        height: '32px',
    },
}));

const Searchbar: React.FC<IProps> = (props: IProps) => {
    const { doSearch, setSearchValue, placeholder = '', searchTerm, searchValue, fullWidth = false } = props;
    const { isDesktop } = useResponsiveHelper();
    const classes = useStyles(fullWidth);

    const handleClear = () => {
        setSearchValue('');
    };

    return (
        <Box height="69px" display="flex" width="100%" justifyContent="center">
            <Paper component="form" className={classes.searchBar} sx={{ borderRadius: '100px', marginRight: isDesktop ? '1.25rem' : null }}>
                <IconButton
                    sx={{ width: '50px' }}
                    type="button"
                    aria-label="search"
                    onClick={(event) => {
                        if (searchTerm && searchValue) {
                            searchTerm();
                        } else if (doSearch) {
                            doSearch(event);
                        } else {
                            return null;
                        }
                    }}
                >
                    <SearchIcon className={classes.searchIcon} />
                </IconButton>
                <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
                    <InputBase
                        className={classes.searchText}
                        autoFocus={true}
                        placeholder={placeholder}
                        value={searchValue}
                        sx={{ color: `${ATCOPaletteColor.shadeBlack} !important` }}
                        inputProps={{
                            onChange: (evt: React.ChangeEvent<HTMLInputElement>) => {
                                setSearchValue(evt.target.value);
                            },
                            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    if (searchTerm && searchValue) {
                                        searchTerm();
                                    } else if (doSearch) {
                                        doSearch(event);
                                    } else {
                                        return null;
                                    }
                                }
                            },
                        }}
                    />
                </Box>
                <IconButton type="button" aria-label="close" disableRipple onClick={handleClear} sx={{ paddingRight: '16px' }}>
                    <CloseIcon className={classes.searchIcon} />
                </IconButton>
            </Paper>
        </Box>
    );
};

export default Searchbar;
